import React, { useContext, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImageIcon } from 'lucide-react';
import { AuthContext } from '@/App';
import MediaPickerModal from '@/components/MediaPickerModal';
import { clearHeroImageCache } from '@/hooks/useHeroImage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Inline "Change image" overlay for page heroes (admin-only).
 * Drop inside a position:relative hero container: <HeroImageEditor pageName="courses" />.
 * Opens the universal picker, saves the chosen URL to the hero image, and refreshes.
 */
export default function HeroImageEditor({ pageName }) {
  const { user } = useContext(AuthContext);
  const [showPicker, setShowPicker] = useState(false);
  const [saving, setSaving] = useState(false);

  if (!user || user.role !== 'admin') return null;

  const handleSelect = async (url) => {
    setShowPicker(false);
    setSaving(true);
    try {
      await axios.put(
        `${API}/admin/hero-images/${pageName}?image_url=${encodeURIComponent(url)}`,
        {},
        { withCredentials: true }
      );
      clearHeroImageCache(pageName);
      toast.success('Hero image updated');
      setTimeout(() => window.location.reload(), 500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update hero image');
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setShowPicker(true)}
        disabled={saving}
        data-testid={`hero-edit-${pageName}`}
        className="absolute top-4 right-4 z-30 inline-flex items-center gap-2 bg-black/70 hover:bg-black text-white text-sm font-semibold px-3 py-2 rounded-lg backdrop-blur transition-colors"
      >
        <ImageIcon className="w-4 h-4" /> {saving ? 'Updating…' : 'Change image'}
      </button>
      <MediaPickerModal isOpen={showPicker} onClose={() => setShowPicker(false)} onSelect={handleSelect} />
    </>
  );
}
